import { Box, Typography } from "@mui/material";
import React, { useCallback, useRef } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import QuotationTable from "../../../components/Table/DocumentTable/QuotationTable";
import { getAllQuotations } from "../../../features/Sales/Quotation/quotation-actions";
import { useSnackbar } from "notistack";
import { filterDateWithUnix } from "../../../utils/filterparams";
import { quotationActions } from "../../../features/Sales/Quotation/quotation-slice";
import { usePermission } from "../../../hooks/use-auth";

const Quotation = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gridRef = useRef();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isFilter = searchParams.get("filter");
  const navigate = useNavigate();
  const { createPermission } = usePermission();

  const breadcrumbs = [
    {
      name: t("sales.index"),
      to: "/sales",
    },
    {
      name: t("sales.quotation.index"),
    },
  ];

  const currentTab = pathname + (isFilter ? `?filter=${isFilter}` : "");

  const tabs = [
    {
      label: t("inventory.all"),
      path: `${pathname}`,
    },
    {
      label: t("status.draft"),
      path: `${pathname}?filter=draft`,
    },
    {
      label: t("status.waitApprove"),
      path: `${pathname}?filter=waitApprove`,
    },
    {
      label: t("status.notApproved"),
      path: `${pathname}?filter=notApproved`,
    },
    {
      label: t("status.waitAccept"),
      path: `${pathname}?filter=waitAccept`,
    },
    {
      label: t("status.accepted"),
      path: `${pathname}?filter=accepted`,
    },
    {
      label: t("status.expired"),
      path: `${pathname}?filter=expired`,
    },
    {
      label: t("status.finished"),
      path: `${pathname}?filter=finished`,
    },
    {
      label: t("status.cancelled"),
      path: `${pathname}?filter=cancelled`,
    },
  ];

  const datasource = {
    getRows(params) {
      const request = params.request;
      const { filterModel } = request;
      const {
        document_id,
        contact_name,
        issue_date,
        due_date,
        created_by,
        employee_list,
        is_late,
      } = filterModel;

      const formatFilterModel = {
        ...request.filterModel,
        document_id: document_id && {
          ...document_id,
          mode: "insensitive",
        },
        contact_name: contact_name && {
          ...contact_name,
          mode: "insensitive",
        },
        issue_date: issue_date && filterDateWithUnix(issue_date),
        due_date: due_date && filterDateWithUnix(due_date),
        employee_list: employee_list && {
          filterType: "objectArray",
          type: "some",
          filter: {
            first_name: { ...employee_list, mode: "insensitive" },
          },
        },
        created_by: created_by && {
          filterType: "object",
          filter: {
            first_name: {
              ...created_by,
              mode: "insensitive",
            },
          },
        },
        is_late: is_late
          ? {
              filterType: "boolean",
              type: "equals",
              filter: "true",
            }
          : undefined,
      };
      dispatch(
        getAllQuotations(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: formatFilterModel,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar
        )
      );
    },
  };

  const onFilterChanged = useCallback(
    (params) => {
      const instance = params.api.getFilterInstance("render_status");
      const isExpired = params.api.getFilterInstance("is_late");
      switch (isFilter) {
        case "draft":
          instance.setModel({ values: ["draft"] });
          isExpired.setModel({});
          break;
        case "waitApprove":
          instance.setModel({ values: ["PENDING"] });
          isExpired.setModel({});
          break;
        case "notApproved":
          instance.setModel({ values: ["DECLINED"] });
          isExpired.setModel({});
          break;
        case "waitAccept":
          instance.setModel({ values: ["waitAccept"] });
          isExpired.setModel({});
          break;
        case "accepted":
          instance.setModel({ values: ["accepted"] });
          isExpired.setModel({});
          break;
        case "expired":
          instance.setModel({});
          isExpired.setModel({ values: ["เกินเวลาแล้ว"] });
          break;
        case "finished":
          instance.setModel({ values: ["finished"] });
          isExpired.setModel({});
          break;
        case "cancelled":
          instance.setModel({ values: ["cancelled"] });
          isExpired.setModel({});
          break;
        default:
          instance.setModel({
            values: [
              "draft",
              "PENDING",
              "DECLINED",
              "waitAccept",
              "accepted",
              "เกินเวลาแล้ว",
              "finished",
            ],
          });
          isExpired.setModel({});
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
    return () => {
      dispatch(quotationActions.resetAllQuotations());
    };
  }, [dispatch, onFilterChanged]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">{t("sales.quotation.index")}</Typography>
        {createPermission && (
          <Box>
            <CustomizedButton
              sx={{ mr: 2 }}
              title={"สร้างใบเสนอราคา"}
              variant="contained"
              onClick={() => navigate(`${pathname}/add`)}
            />
          </Box>
        )}
      </Box>
      <CustomizedTab table tabs={tabs} currentTab={currentTab} divider />
      <QuotationTable gridRef={gridRef} onGridReady={onGridReady} />
    </>
  );
};

export default Quotation;
